import Vue from 'vue';
import { Component, Watch } from 'vue-property-decorator';
import { ui } from '@/constants';
import { BuilderMode, ItemCardCustomizationType, ItemCardSelectionInfoKind, MenuItem, OrderMeal, OrderMealRequest, OrderWithContext, PortionItemType, RecentOrFavoriteEntree, SelectableItemCardCustomization, SelectableVariantMenuItem } from '@/models';
import intersectionWith from 'lodash/intersectionWith';
import { ErrorEvent } from '@/services/analytics/events/error-event';
import { AdobeCartAddEvent } from '@/services/analytics/platforms/adobe-analytics/adobe-events';
import { AddToBagEvent } from '@/services/analytics/events';
import { AnalyticsMealType } from '@/services/analytics/platforms/adobe-analytics/models';
import { MUTATIONS } from '@/store/adobe';

@Component({
  name: 'CmgOneTapReorderUnavailabilityModal',
})
export default class CmgOneTapReorderUnavailabilityModal extends Vue {
  isLoading = false;
  isMissingRequiredContents = false;
  isMissingOptionalItems = false;
  isVariantAvailable = false;  

  get visible(): boolean {
    return this.$appStore.state.ui.modals.oneTapReorderUnavailabilityModal.visible;
  }

  get args() {
    return this.$appStore.state.ui.modals.oneTapReorderUnavailabilityModal.args;
  }

  get isMobile(): boolean {
    return this.$appStore.state.ui.clientWidth <= ui.breakpoints.phoneLgMax;
  }

  get entree(): RecentOrFavoriteEntree | undefined {
    return this.args?.entree;
  }

  get unavailableItems(): MenuItem[] {
    return this.args?.unavailableItems || [];
  }

  get modificationRequired(): boolean {
    return this.isMissingRequiredContents || !this.isVariantAvailable;
  }

  get ctaText(): string {
    return this.modificationRequired ? this.modificationRequiredText : this.modificationOptionalText;
  }

  get hasMultipleUnavailableItems(): boolean {
    return (this.args && this.args.unavailableItems.length > 1) || false;
  }

  get modificationRequiredText(): string {
    return this.hasMultipleUnavailableItems ? 'You must select new required ingredients to continue.' : 'You must select a new required ingredient to continue.'
  }

  get modificationOptionalText(): string {
    return this.hasMultipleUnavailableItems ? 'Add to bag to continue without these ingredients or modify to make another selection.' : 'Add to bag to continue without this ingredient or modify to make another selection.'
  }

  get builderMode(): BuilderMode {
    return this.$appStore.state.mealBuilder.builderMode!;
  }

  get selectedVariant(): SelectableVariantMenuItem | null {
    return this.$appStore.state.mealBuilder.selectedVariant;
  }

  get displayedVariants(): SelectableVariantMenuItem[] {
    return this.$appStore.state.mealBuilder.displayedVariants;
  }

  // Menu Item Deactivation type for FullStory
  get midType(): string {
    return this.modificationRequired && this.isMissingOptionalItems ? 'required, optional' : this.modificationRequired ? 'required': 'optional';
  }

  @Watch('visible')
  async onVisibleUpdated(visible: boolean) {
    if (!visible || !this.args) {
      return;
    }
    this.isLoading = false;
    this.checkForMissingRequiredItems();
  }

  onCancel() {
    this.close();
  }

  async onModify() {
    if (this.args) {
      this.isLoading = true;

      try {
        this.setUpEditRecentEntree();
        this.close();
      } finally {
        this.isLoading = false;
      }
    }
  }

  async onAddToBag() {
    if (this.args) {
      this.isLoading = true;

      try {
        if (this.entree) {
          this.removeUnavailableItems();
          this.args.onAddToBag(this.entree);
          this.close();
        }
      } finally {
        this.isLoading = false;
      }
    }
  }

  private close() {
    this.$services.ui.setModalVisibility('oneTapReorderUnavailabilityModal', false);
    this.$services.ui.focusBack();
  }

  private removeUnavailableItems() {
    if (this.entree?.contents) {
      this.entree!.contents = this.entree?.contents?.filter(c => c.isItemAvailable);
    }
  }

  private checkForMissingRequiredItems() {
    this.isMissingRequiredContents = false;
    this.isMissingOptionalItems = false;
    this.isVariantAvailable = !this.unavailableItems.find(i => i.itemId == this.entree!.menuItemId);
    
    const variant = this.selectedVariant ?? (this.$services.mealBuilder.getDefaultVariant(this.builderMode) ?? null); 
    const sections = this.$services.mealBuilder.getItemSections(this.builderMode, variant);    
    
    for (const section of sections) {
      for (const selectableItem of section.items) {
        const menuItem = this.entree?.contents!.find(x => x.menuItemId == selectableItem.item.itemId);
        const matchingUnavailableItem = this.unavailableItems.find(i => i.itemId == menuItem?.menuItemId);  

        if (!!matchingUnavailableItem) {
          // if there is an unavailable item in the reorder, get quantity of content items in order to
          // check minimum quantity is met for required items.
          const contentQuantity = intersectionWith(this.entree?.contents, section.items, (a, b) => {
            return a.menuItemId == b.item?.itemId;
          }).length;

          if ((section.minQuantity && section.minQuantity >= contentQuantity) || section.showSelectNone) {
            this.isMissingRequiredContents = true;
          } else {
            this.isMissingOptionalItems = true;
          }
        }
      }
    }
    if (this.modificationRequired || this.isMissingOptionalItems){
      this.sendErrorAnalyticsEvent('mid',this.ctaText, 'one-tap-reorder', this.midType);
    }
  }

  private setUpEditRecentEntree() {
    // Reset the builder since the customer could have been selecting 
    // items in the meal builder before deciding to modify a recent entree
    const menu = this.$appStore.state.menu.activeMenu;
    const metadata = this.$appStore.state.menu.metadata;
    const group = this.$appStore.state.mealBuilder.builderMode?.group;
    this.$services.analytics.setIfEditingOneTap(true);

    if (!menu || !metadata || !group) {
      return;
    }

    this.$services.mealBuilder.refreshBuilder(menu, metadata, group, true);

    // Note: This mimics quite a bit of the functionality that exists on cmg-meal-builder on mount (i.e. setupEditMode and setupPcmEditMode). 
    // If this functionality of editing a recent entree (that is not a PCE, nor a meal already added to the bag) is used more often,
    // we will need to look into adding a recentEntreeId or something similar to a route param for the meal builder page, so that this loading
    // of an edited recent entree can happen from multiple locations 
    const entreeVariant = this.isVariantAvailable ? this.displayedVariants.find(v => v.item.itemId == this.entree!.menuItemId) : null;

    if (entreeVariant && this.builderMode) {
      this.$services.mealBuilder.selectVariantSafe(this.builderMode, entreeVariant, null, this.displayedVariants, null, false, true);
    }

    const sections = this.$services.mealBuilder.getItemSections(this.builderMode, entreeVariant ?? (this.$services.mealBuilder.getDefaultVariant(this.builderMode) ?? null));
    for (const section of sections) {
      for (const selectableItem of section.items) {
        const menuItem = this.entree?.contents!.find(x => x.menuItemId == selectableItem.item.itemId);
        const matchingUnavailableItem = this.unavailableItems.find(i => i.itemId == menuItem?.menuItemId);

        if (!menuItem || !!matchingUnavailableItem) {
          continue;
        }

        if (selectableItem.item.pricingReferenceItemId && selectableItem.item.itemType === PortionItemType.HalfPortion) {
          const referencedVariant = this.displayedVariants.find(v => v.item.itemId == selectableItem.item.pricingReferenceItemId);

          if (referencedVariant && referencedVariant.item.isItemAvailable) {
            this.$services.mealBuilder.selectVariantSafe(this.builderMode, referencedVariant, this.selectedVariant, this.$appStore.state.mealBuilder.displayedVariants, null, false, true);
          }
        } else if (selectableItem.item.pricingReferenceItemId && selectableItem.item.itemType === PortionItemType.ExtraPortion) {
          const referencedVariant = this.displayedVariants.find(v => v.item.itemId == selectableItem.item.pricingReferenceItemId);

          if (referencedVariant && referencedVariant.item.isItemAvailable) {
            // If we've iterated to the double portion before we selected the base variant, select the variant first
            if (!this.$services.mealBuilder.isItemDisplayedAsSelected(this.builderMode, selectableItem)) {
              this.$services.mealBuilder.selectVariantSafe(this.builderMode, selectableItem as SelectableVariantMenuItem, this.selectedVariant, this.displayedVariants, true, true);

              const extraPortion = this.getExtraPortionCustomizationForVariant(referencedVariant);
              if (extraPortion) {
                this.$services.mealBuilder.toggleVariantCustomizationSafe(this.builderMode, this.selectedVariant!, this.displayedVariants, extraPortion);
              }
            }
          }
        } else {
          const args = selectableItem.selectionInfo.kind == ItemCardSelectionInfoKind.Quantity ? menuItem.quantity : true;
          const itemVariant = this.selectedVariant ?? (this.$services.mealBuilder.getDefaultVariant(this.builderMode) ?? null);
          
          if (!this.$services.mealBuilder.isItemDisplayedAsSelected(this.builderMode, selectableItem))  {
            this.$services.mealBuilder.selectItemSafe(selectableItem, section, this.$services.mealBuilder.getDefaultVariant(this.builderMode) ?? null, args, undefined, false);
          }
          
          if (itemVariant) {
            if (menuItem.customizationId) {
              const customization = selectableItem.customizations.find(
                c => c.type == ItemCardCustomizationType.Customization && c.customization.id == menuItem.customizationId,
              );
  
              if (customization && !customization.selected) {
                this.$services.mealBuilder.toggleItemCustomizationSafe(selectableItem, customization, section, itemVariant, undefined, false);
              }
            }

            for (const variantCustomization of itemVariant.customizations) {
              if (variantCustomization.type == ItemCardCustomizationType.SelectableItem && variantCustomization.portion == selectableItem.item.itemType) {
                variantCustomization.selected = true;
              }
            }
          }
        }
      }

      if (!this.$services.mealBuilder.getSelectedItems(section.items).length && section.showSelectNone) {
        this.$services.mealBuilder.optOutOfItemSection(section, false);
      }
    }
  }

  getExtraPortionCustomizationForVariant(variant: SelectableVariantMenuItem): SelectableItemCardCustomization | undefined {
    return variant.customizations.find(c => c.type == ItemCardCustomizationType.SelectableItem && c.portion == PortionItemType.ExtraPortion) as
    | SelectableItemCardCustomization | undefined;
  }

  private sendErrorAnalyticsEvent(errorType : string, errorName : string,  action : string, midType: string) : void {
    this.$services.analytics.send(new ErrorEvent(errorType, errorName, action, midType));
  }

  private sendBagAnalyticsEvent(order: OrderWithContext, orderMealRequest: OrderMealRequest, isOrderStart: boolean) : void {
    const meal = orderMealRequest.meal as OrderMeal;
    this.$services.analytics.setAddToBagEventToStore(new AdobeCartAddEvent(new AddToBagEvent(meal, AnalyticsMealType.Standard, isOrderStart, undefined, false, 'one-tap-reorder'), this.$appStore, this.$services));
  }
}
